// @flow

import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import actionCreators from "@modules/client/actionCreator";
import * as clientsSelectors from "@modules/client/selectors";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, View } from "@components";
import { SchemaForm } from "@components";
import uiSchema from "./ui-schema";
import type { Client } from "@modules/client/types";
import type { State } from "@store/reducers";

type OwnProps = {
  clientId: string,
  onClose: () => void,
  onSubmit: (client: Client) => void,
};

type PropsFromState = {
  schema: Object,
};

type PropsFromDispatch = {
  getClientSchema: typeof actionCreators.GetClientsSchema,
  onSubmit: typeof actionCreators.AddClientDetails,
};

type Props = {
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
};

const DEFAULT_COUNTRY_ID = 1;
const DEFAULT_SECTOR = "PUBLIC";

const ClientModal = props => {
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    region: {},
    country: { id: DEFAULT_COUNTRY_ID },
    sector: DEFAULT_SECTOR,
    status: "INACTIVE",
    inTesting: false,
  });

  useEffect(() => {
    if (!props.schema) {
      props.getClientSchema();
    }
  }, []);

  const submitForm = () => {
    formRef.current.submit();
  };

  const getUiSchema = () => {
    return uiSchema(props.t);
  };

  const getSchema = () => {
    const schema = props.schema;
    if (!schema) {
      return null;
    }

    const formatedSchema = {
      ...schema,
      dependencies: {
        ...schema.dependencies,
        ...schema.properties.address.dependencies,
      },
      properties: {
        ...schema.properties,
        accountManager: {
          ...schema.properties.accountManager,
          properties: {
            ...schema.properties.accountManager.properties,
            id: {
              ...schema.properties.accountManager.properties.id,
              title: props.t("MANAGER"),
            },
          },
        },
        reseller: {
          ...schema.properties.reseller,
          properties: {
            ...schema.properties.reseller.properties,
            id: {
              ...schema.properties.reseller.properties.id,
              title: props.t("OWNER"),
            },
          },
        },
        ...schema.properties.address.properties,
      },
      required: [...schema.required, "addressLine1", "postCode"].filter(
        field => field !== "address",
      ),
    };

    delete formatedSchema.properties.address;
    return formatedSchema;
  };

  const handleSubmit = () => {
    const data = {
      ...formData,
      address: {
        country: formData.country,
        region: formData.region,
        postCode: formData.postCode,
        addressLine1: formData.addressLine1,
        addressLine2: null,
        addressLine3: null,
      },
    };

    delete data.country;
    delete data.region;
    delete data.postCode;
    delete data.addressLine1;
    props.onSubmit(data);
  };

  const handleChange = (data: Object) => {
    setFormData(data);
  };

  return (
    <Modal className="modal-normal">
      <ModalHeader toggle={props.onClose} className="d-flex justify-content-between">
        {props.t("ADD_CLIENT")}
      </ModalHeader>
      <ModalBody>
        <View padding="0.25rem 0.5rem">
          {getSchema() && (
            <SchemaForm
              ref={formRef}
              className="w-100"
              formData={formData}
              schema={getSchema()}
              uiSchema={getUiSchema()}
              onSubmit={handleSubmit}
              onChange={handleChange}
              externalSubmit
              boxGrid
            />
          )}
        </View>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <Button color="secondary" onClick={props.onClose}>
          {props.t("CANCEL")}
        </Button>
        <Button materialIcon="replay_circle_filled" onClick={submitForm}>
          {props.t("SUBMIT")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const LocalizedAddClientModal = localize()(ClientModal);

const mapStateToProps = (state: State): PropsFromState => ({
  schema: clientsSelectors.getClientsSchema(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getClientSchema: actionCreators.GetClientsSchema,
  onSubmit: actionCreators.AddClientDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(LocalizedAddClientModal);
